import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/api";
import { CheckCircle2, ArrowRight, Upload, BookText, Users, Check, BadgeCheck } from "lucide-react";

const FREE_PLAN = {
  id: "free",
  name: "Free Forever",
  tagline: "Start here, stay as long as you like",
  storage: "500MB",
  athletes: 1,
  price: 0,
  features: [
    "1 athlete profile",
    "500MB storage",
    "Watermarked memories",
    "Basic journaling",
    "The Loop (view-only)",
  ],
};

const FAMILY_MONTHLY = {
  id: "mid",
  price: 12.99,
  priceId: import.meta.env.VITE_STRIPE_PRICE_ID_FAMILY_MONTHLY as string | undefined,
};

const PLANS = [
  {
    id: "basic",
    name: "Loop Family",
    tagline: "The whole journey, one home",
    storage: "1TB",
    athletes: 2,
    price: 99,
    period: "year",
    priceId: import.meta.env.VITE_STRIPE_PRICE_ID_FAMILY_ANNUAL as string | undefined,
    features: [
      "2 athlete profiles",
      "1TB storage",
      "No watermarks",
      "HD video support",
      "AI journaling prompts",
      "The Loop sharing and Loop Links",
    ],
    popular: true,
  },
  {
    id: "premium",
    name: "Loop Recruit",
    tagline: "For families on the recruiting path",
    storage: "2TB",
    athletes: 2,
    price: 199,
    period: "year",
    priceId: import.meta.env.VITE_STRIPE_PRICE_ID_RECRUIT as string | undefined,
    features: [
      "Everything in Loop Family",
      "2TB storage",
      "Full Recruiting Center access",
      "AI coach email writer",
      "College interest tracker",
      "Priority feature access",
    ],
    popular: false,
  },
] as const;

const PLAN_NAME_BY_ID: Record<string, string> = {
  basic: "Loop Family",
  mid: "Loop Family",
  premium: "Loop Recruit",
};

export default function Subscription() {
  const [, setLocation] = useLocation();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [err, setErr] = useState<string>("");

  const { data: currentUser } = useQuery<{ subscriptionTier?: string }>({
    queryKey: ["/api/user"],
  });
  const currentTier = currentUser?.subscriptionTier ?? "free";

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const status = searchParams.get("status");
  const checkoutTier = useMemo(() => {
    return (
      searchParams.get("tier") ||
      window.sessionStorage.getItem("legacyloop_last_checkout_plan") ||
      ""
    );
  }, [searchParams]);

  const isCheckoutSuccess = status === "success";
  const isCheckoutCanceled = status === "cancel";
  const tierName = PLAN_NAME_BY_ID[checkoutTier] || "your new plan";
  const dashboardTarget = checkoutTier
    ? `/dashboard?welcome=1&tier=${encodeURIComponent(checkoutTier)}`
    : "/dashboard?welcome=1";

  useEffect(() => {
    if (!isCheckoutSuccess) return;
    window.sessionStorage.removeItem("legacyloop_last_checkout_plan");
    const timer = window.setTimeout(() => {
      setLocation(dashboardTarget);
    }, 2600);
    return () => window.clearTimeout(timer);
  }, [dashboardTarget, isCheckoutSuccess, setLocation]);

  async function handleManageSubscription() {
    setErr("");
    setPortalLoading(true);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) { setLocation("/login?redirect=/subscription"); return; }

      const res = await apiRequest("/billing-portal", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Could not open billing portal.");
      const { url } = await res.json();
      if (url) window.location.href = url;
    } catch (e: any) {
      setErr(e?.message || "Something went wrong.");
    } finally {
      setPortalLoading(false);
    }
  }

  async function handleUpgrade(planId: string, priceId?: string) {
    if (!priceId) {
      setErr("This plan is not available yet. Please try again later.");
      return;
    }

    setErr("");
    setLoadingPlanId(planId);
    window.sessionStorage.setItem("legacyloop_last_checkout_plan", planId);

    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;

      if (!token) {
        setLocation(`/login?redirect=/subscription`);
        return;
      }

      const requestBody = JSON.stringify({ priceId, planId });
      let res = await apiRequest("/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: requestBody,
      });

      if (res.status === 401) {
        const refreshed = await supabase.auth.refreshSession();
        const refreshedToken = refreshed.data.session?.access_token;
        if (refreshedToken) {
          res = await apiRequest("/checkout", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${refreshedToken}` },
            body: requestBody,
          });
        }
      }

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        if (res.status === 401) {
          setErr("Your session could not be verified. Please sign out and sign back in.");
          return;
        }
        throw new Error(payload?.error || "Unable to start checkout.");
      }

      const payload = await res.json();
      if (payload?.url) {
        window.location.href = payload.url;
      } else {
        throw new Error("Checkout link missing.");
      }
    } catch (e: any) {
      setErr(e?.message || "Something went wrong.");
    } finally {
      setLoadingPlanId(null);
    }
  }

  if (isCheckoutSuccess) {
    return (
      <div className="min-h-screen bg-transparent text-white">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="rounded-xl border border-[#E0A458] bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] p-8 shadow-lg shadow-black/25">
            <div className="flex items-center gap-2 text-[#E0A458] text-sm font-semibold uppercase tracking-wide mb-3">
              <CheckCircle2 className="h-4 w-4" />
              Plan Activated
            </div>
            <h1 className="font-montserrat font-bold text-3xl text-white mb-1">Welcome to {tierName}</h1>
            <p className="text-slate-400 text-sm mb-8">Your subscription is active. Taking you to your dashboard now.</p>

            <p className="text-sm font-medium text-slate-300 mb-3">Quick actions</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
              <Link href="/media">
                <Button className="w-full bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold">
                  <Upload className="mr-2 h-4 w-4" /> Upload a highlight
                </Button>
              </Link>
              <Link href="/journal">
                <Button className="w-full bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold">
                  <BookText className="mr-2 h-4 w-4" /> Start a journal entry
                </Button>
              </Link>
              <Link href="/family">
                <Button className="w-full bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold">
                  <Users className="mr-2 h-4 w-4" /> Invite your family
                </Button>
              </Link>
            </div>

            <div className="flex justify-center">
              <Button
                onClick={() => setLocation(dashboardTarget)}
                className="bg-white/10 text-white hover:bg-white/20 border border-white/20"
              >
                Go to Dashboard
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">

        <div className="rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-6 py-6 shadow-lg shadow-black/25 mb-10 text-center">
          <h1 className="font-montserrat font-bold text-3xl text-white">Choose the plan that protects every memory</h1>
          <p className="text-slate-400 text-sm mt-2 max-w-xl mx-auto">
            Free accounts are a great start. Upgrade to unlock full storage, AI features, and a private vault your whole family can share.
          </p>
        </div>

        {isCheckoutCanceled && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-300 text-sm text-center mb-6">
            Checkout was canceled. Pick any plan below to continue.
          </div>
        )}

        {/* Free plan — shown as current plan strip when user is on free tier */}
        {currentTier === "free" && (
          <div className="relative mb-8">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
              <span className="bg-[#1B263B] text-[#E0A458] border border-[#E0A458]/40 font-bold px-3 py-1 text-xs rounded-full flex items-center gap-1">
                <BadgeCheck className="h-3 w-3" /> Your Current Plan
              </span>
            </div>
            <div className="rounded-xl border border-[#E0A458]/30 bg-[#0D1B2A] px-6 py-5 pt-7">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="font-montserrat font-bold text-xl text-white">{FREE_PLAN.name}</h2>
                  <p className="text-xs text-slate-400 mt-0.5">{FREE_PLAN.tagline}</p>
                </div>
                <div className="flex items-baseline gap-1 shrink-0">
                  <span className="text-3xl font-bold text-white">$0</span>
                  <span className="text-slate-400 text-sm">/ month</span>
                </div>
              </div>
              <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-1.5">
                {FREE_PLAN.features.map((f) => (
                  <li key={f} className="flex items-center gap-1.5 text-sm text-slate-300">
                    <Check className="h-3.5 w-3.5 text-[#E0A458] shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {PLANS.map((plan) => {
            const isLoading = loadingPlanId === plan.id;
            const isAvailable = !!plan.priceId;
            const isCurrent =
              plan.id === currentTier ||
              (plan.id === "basic" && currentTier === "mid");

            return (
              <div key={plan.id} className="relative flex flex-col">
                {isCurrent ? (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                    <span className="bg-[#1B263B] text-[#E0A458] border border-[#E0A458]/40 font-bold px-3 py-1 text-xs rounded-full flex items-center gap-1">
                      <BadgeCheck className="h-3 w-3" /> Your Plan
                    </span>
                  </div>
                ) : plan.popular && currentTier === "free" ? (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                    <span className="bg-[#E0A458] text-black font-bold px-3 py-1 text-xs rounded-full">Most Popular</span>
                  </div>
                ) : null}
                <div
                  className={`flex flex-col flex-1 rounded-xl p-6 transition-all ${
                    isCurrent
                      ? "border-2 border-[#E0A458]/50 bg-[#0D1B2A] pt-8 opacity-80"
                      : plan.popular && currentTier === "free"
                      ? "border-2 border-[#E0A458] bg-[#0D1B2A] pt-8 cursor-pointer"
                      : "border border-[#E0A458]/20 bg-[#0D1B2A] hover:border-[#E0A458]/60 cursor-pointer"
                  }`}
                  onClick={() => !isCurrent && isAvailable && handleUpgrade(plan.id, plan.priceId)}
                  role={isCurrent ? undefined : "button"}
                  tabIndex={isCurrent ? undefined : 0}
                  onKeyDown={(e) => {
                    if (isCurrent || !isAvailable) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleUpgrade(plan.id, plan.priceId);
                    }
                  }}
                >
                  <h2 className="font-montserrat font-bold text-xl text-white mb-0.5">{plan.name}</h2>
                  <p className="text-xs text-slate-400 mb-4">{plan.tagline}</p>

                  <div className="flex items-baseline gap-1 mb-5">
                    <span className="text-4xl font-bold text-white">${plan.price}</span>
                    <span className="text-slate-400 text-sm">/ {plan.period}</span>
                  </div>

                  <ul className="space-y-2 mb-6 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                        <Check className="h-3.5 w-3.5 text-[#E0A458] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {isCurrent ? (
                    <Button
                      disabled
                      className="w-full bg-[#1B263B] text-[#E0A458] border border-[#E0A458]/30 font-semibold mt-auto cursor-default"
                    >
                      <BadgeCheck className="h-4 w-4 mr-2" /> Current Plan
                    </Button>
                  ) : (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpgrade(plan.id, plan.priceId);
                      }}
                      disabled={isLoading || !isAvailable}
                      className="w-full bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold mt-auto"
                    >
                      {isLoading ? "Redirecting…" : "Choose This Plan"}
                    </Button>
                  )}

                  {plan.id === "basic" && !isCurrent && FAMILY_MONTHLY.priceId && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpgrade(FAMILY_MONTHLY.id, FAMILY_MONTHLY.priceId);
                      }}
                      disabled={loadingPlanId === FAMILY_MONTHLY.id}
                      className="text-xs text-slate-400 hover:text-[#E0A458] underline underline-offset-2 text-center mt-3 bg-transparent border-0 cursor-pointer"
                    >
                      {loadingPlanId === FAMILY_MONTHLY.id
                        ? "Redirecting…"
                        : `or $${FAMILY_MONTHLY.price}/month, billed monthly`}
                    </button>
                  )}

                  {!isAvailable && !isCurrent && (
                    <p className="text-xs text-slate-500 text-center mt-2">Pricing being finalized. Check back soon.</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {err && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-400 text-sm text-center mt-6">
            {err}
          </div>
        )}

        {currentTier !== "free" && (
          <div className="mt-10 rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-montserrat font-bold text-white text-sm">Manage your subscription</p>
              <p className="text-slate-400 text-xs mt-0.5">Update your payment method, view invoices, or cancel anytime.</p>
            </div>
            <Button
              variant="outline"
              className="border-[#415A77] text-slate-300 hover:bg-[#1B263B] shrink-0"
              onClick={handleManageSubscription}
              disabled={portalLoading}
            >
              {portalLoading ? "Opening…" : "Manage Subscription"}
            </Button>
          </div>
        )}

        <p className="text-center text-slate-400 text-sm mt-8">
          All plans include The Loop, Media Vault, Journal, and Recruiting Center. Cancel any time.
        </p>

      </div>
    </div>
  );
}
